import { useEffect, useRef, useCallback } from 'react';
import { useAnalyticsWithConsent } from '../../../hooks/useAnalyticsWithConsent';
import { AnalyticsEvents } from '../../../analytics/firebaseAnalytics';
import { SearchParams } from '../BrowseListingsPage';
import { Listing } from '../../../components/property/property-card/PropertyCardTypes';
import { useAuth } from '../../../auth/firebaseAuth';

interface UseSearchAnalyticsResult {
  handleListingView: (
    listing: Listing,
    currentPage: number,
    listings: Listing[],
    searchParams: SearchParams
  ) => void;
  trackSearchResults: (
    totalListings: number,
    searchParams: SearchParams,
    sortOption: string
  ) => void;
  trackSessionEnd: (bookmarkedListings: number[]) => void;
}

export const useSearchAnalytics = (): UseSearchAnalyticsResult => {
  const analytics = useAnalyticsWithConsent();
  const { currentUser } = useAuth();
  const sessionStart = useRef(new Date());
  const searchStartTime = useRef<Date>();
  const interactionCount = useRef(0);
  const viewedListings = useRef<Set<number>>(new Set());
  const searchHistory = useRef<SearchParams[]>([]);

  // Helper functions
  const hasActiveFilters = (params: SearchParams): boolean => {
    return Object.entries(params).some(([key, value]) => {
      if (key === 'location') return !!value;
      if (key === 'availableFrom') return !!value;
      if (typeof value === 'number') return value > 0;
      return value !== 'Any';
    });
  };

  const getActiveFilters = (params: SearchParams): string[] => {
    return Object.entries(params)
      .filter(([key, value]) => {
        if (key === 'location') return !!value;
        if (key === 'availableFrom') return !!value;
        if (typeof value === 'number') return value > 0;
        return value !== 'Any';
      })
      .map(([key]) => key);
  };

  const handleListingView = useCallback(
    (listing: Listing, currentPage: number, listings: Listing[], searchParams: SearchParams) => {
      if (!viewedListings.current.has(listing.id)) {
        viewedListings.current.add(listing.id);

        const positionInResults = listings.findIndex((l) => l.id === listing.id) + 1;
        const isTopViewed = positionInResults <= 3; // First 3 properties are top viewed

        analytics.trackPropertyView(listing.id, {
          type: listing.type,
          price: listing.price,
          location: listing.location,
          position_in_results: positionInResults,
          page_number: currentPage,
          source: 'search_results',
          search_params: searchParams,
          is_top_viewed: isTopViewed, // Track if this is a top viewed property
        });
      }
    },
    [analytics]
  );

  const trackSearchResults = useCallback(
    (totalListings: number, searchParams: SearchParams, sortOption: string) => {
      if (searchStartTime.current) {
        const searchDuration = new Date().getTime() - searchStartTime.current.getTime();

        analytics.logEvent(AnalyticsEvents.SEARCH_RESULTS, {
          results_count: totalListings,
          search_duration: searchDuration,
          zero_results: totalListings === 0,
          has_filters: hasActiveFilters(searchParams),
          filters_used: getActiveFilters(searchParams),
          sort_option: sortOption,
        });
      }
    },
    [analytics]
  );

  const trackSessionEnd = useCallback(
    (bookmarkedListings: number[]) => {
      analytics.logEvent(AnalyticsEvents.SEARCH_SESSION_END, {
        session_duration: new Date().getTime() - sessionStart.current.getTime(),
        total_searches: searchHistory.current.length,
        listings_viewed: viewedListings.current.size,
        interactions_count: interactionCount.current,
        conversion_actions: {
          bookmarks: bookmarkedListings.length,
          shares: 0,
          inquiries: 0,
        },
      });
    },
    [analytics]
  );

  // Track pixel page view
  useEffect(() => {
    analytics.trackPixelPageView({
      email: currentUser?.email || undefined,
      externalId: currentUser?.uid,
    });
  }, [analytics, currentUser]);

  return {
    handleListingView,
    trackSearchResults,
    trackSessionEnd,
  };
};
